import type { LucideIcon } from 'lucide-react';
import {
  CirclePlay,
  Code2,
  FileText,
  Film,
  FolderClosed,
  Image as ImageIcon,
  Music,
  Table,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------------------------------
 * FileTypeIcon - File type indicator with extension badge
 * Supports Default, Accent, and Solid variant styles per Figma spec.
 * -------------------------------------------------------------------------------------------------*/

export type FileExtension =
  | 'pdf'
  | 'doc'
  | 'docx'
  | 'xls'
  | 'xlsx'
  | 'ppt'
  | 'pptx'
  | 'txt'
  | 'csv'
  | 'img'
  | 'jpg'
  | 'jpeg'
  | 'png'
  | 'gif'
  | 'svg'
  | 'webp'
  | 'tiff'
  | 'eps'
  | 'mp3'
  | 'wav'
  | 'mp4'
  | 'mpeg'
  | 'avi'
  | 'mkv'
  | 'mov'
  | 'fig'
  | 'ai'
  | 'psd'
  | 'indd'
  | 'aep'
  | 'html'
  | 'css'
  | 'js'
  | 'json'
  | 'xml'
  | 'sql'
  | 'java'
  | 'rss'
  | 'exe'
  | 'dmg'
  | 'zip'
  | 'rar'
  | string;

export type SimpleFileType =
  | 'empty'
  | 'folder'
  | 'image'
  | 'video'
  | 'play'
  | 'audio'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'code';

export type FileTypeIconVariant = 'default' | 'gray' | 'accent' | 'solid';

/* ---- color configuration ---- */

interface ExtensionStyle {
  /** Background class for badge (default) and page fill (solid) */
  bg: string;
  /** Background class for page fill (accent variant) */
  accent: string;
}

const es = (bg: string, accent: string): ExtensionStyle => ({ bg, accent });

// Shared styles for groups with >=3 identical entries
const PRIMARY = es('bg-primary', 'bg-primary/10');
const SUCCESS = es('bg-success', 'bg-success/10');
const ORANGE_500 = es('bg-orange-500', 'bg-orange-500/10');
const BLUE_500_MUTED = es('bg-blue-500', 'bg-muted');
const INDIGO_600 = es('bg-indigo-600', 'bg-indigo-600/10');
const GRAY_700 = es('bg-gray-700', 'bg-gray-700/10');
const PINK_MUTED = es('bg-pink-600', 'bg-muted');

const extensionStyleMap: Record<string, ExtensionStyle> = {
  // Images
  img: PRIMARY,
  jpg: PRIMARY,
  jpeg: PRIMARY,
  png: PRIMARY,
  webp: PRIMARY,
  tiff: PRIMARY,
  gif: PRIMARY,
  svg: PRIMARY,
  eps: PRIMARY,
  // Documents
  pdf: es('bg-destructive', 'bg-destructive/10'),
  doc: es('bg-blue-300', 'bg-blue-300/15'),
  docx: es('bg-blue-300', 'bg-blue-300/15'),
  txt: es('bg-gray-500', 'bg-gray-500/10'),
  csv: SUCCESS,
  xls: SUCCESS,
  xlsx: SUCCESS,
  ppt: ORANGE_500,
  pptx: ORANGE_500,
  // Design
  fig: PRIMARY,
  ai: es('bg-orange-400', 'bg-orange-400/10'),
  psd: es('bg-blue-500', 'bg-blue-500/10'),
  indd: es('bg-fuchsia-600', 'bg-fuchsia-600/10'),
  aep: es('bg-purple-600', 'bg-purple-600/10'),
  // Media (accent uses muted gray per Figma "Gray" variant)
  mp3: PINK_MUTED,
  wav: PINK_MUTED,
  mp4: BLUE_500_MUTED,
  mpeg: BLUE_500_MUTED,
  avi: BLUE_500_MUTED,
  mkv: BLUE_500_MUTED,
  mov: BLUE_500_MUTED,
  // Development
  html: INDIGO_600,
  css: INDIGO_600,
  rss: ORANGE_500,
  sql: INDIGO_600,
  js: INDIGO_600,
  json: GRAY_700,
  java: ORANGE_500,
  xml: PRIMARY,
  exe: GRAY_700,
  dmg: GRAY_700,
  // Archive
  zip: GRAY_700,
  rar: GRAY_700,
};

/** Backward-compatible extension to badge color mapping */
export const extensionColors: Record<string, string> = Object.fromEntries(
  Object.entries(extensionStyleMap).map(([ext, { bg }]) => [ext, bg]),
);

const fallbackStyle = es('bg-muted-foreground', 'bg-muted');

/* ---- simple file type icons ---- */

const simpleFileTypeIcons: Record<SimpleFileType, LucideIcon | null> = {
  empty: null,
  folder: FolderClosed,
  image: ImageIcon,
  video: Film,
  play: CirclePlay,
  audio: Music,
  pdf: FileText,
  document: FileText,
  spreadsheet: Table,
  code: Code2,
};

/* ---- size tokens ---- */

const sizeMap = {
  xs: 'size-4',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
} as const;
const badgeFont = {
  sm: 'text-[8px]',
  md: 'text-[10px]',
  lg: 'text-[12px]',
} as const;
const labelFont = {
  sm: 'text-[7px]',
  md: 'text-[9px]',
  lg: 'text-[11px]',
} as const;

/* ---- component ---- */

export interface FileTypeIconProps {
  /** File extension (e.g., 'pdf', 'mp4', 'fig') */
  extension?: FileExtension;
  /** Generic file type icon when no extension is provided */
  fileType?: SimpleFileType;
  /** Visual variant */
  variant?: FileTypeIconVariant;
  /** Size of the icon */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

function FileTypeIcon({
  extension,
  fileType,
  variant = 'default',
  size = 'md',
  className,
}: FileTypeIconProps) {
  const normalizedExt = extension?.toLowerCase().replace('.', '');
  const hasExtension = !!normalizedExt;
  const isSimple = !hasExtension && !!fileType;

  const activeStyle = hasExtension
    ? (extensionStyleMap[normalizedExt] ?? fallbackStyle)
    : PRIMARY;
  const label = hasExtension
    ? normalizedExt.toUpperCase().slice(0, 4)
    : undefined;

  const Icon = isSimple && fileType ? simpleFileTypeIcons[fileType] : null;

  const ariaLabel = normalizedExt
    ? `${normalizedExt} file type icon`
    : fileType
      ? `${fileType} file type icon`
      : 'file type icon';

  const useFoldedPage = variant === 'default' && hasExtension;

  return (
    <div
      className={cn('relative', sizeMap[size], className)}
      data-slot="file-type-icon"
      role="img"
      aria-label={ariaLabel}
    >
      {/* Page shape */}
      {useFoldedPage ? (
        <svg
          viewBox="0 0 40 40"
          fill="none"
          className="size-full"
          aria-hidden="true"
        >
          <path
            d="M7 4C7 1.79086 8.79086 0 11 0H25L33 8V36C33 38.2091 31.2091 40 29 40H11C8.79086 40 7 38.2091 7 36V4Z"
            className="fill-muted stroke-border"
            strokeWidth="1"
          />
          <path
            d="M25 0L33 8H27C25.8954 8 25 7.10457 25 6V0Z"
            className="fill-border"
          />
        </svg>
      ) : (
        <div
          className={cn(
            'absolute inset-x-[10%] inset-y-0 rounded',
            variant === 'default'
              ? 'bg-background border border-border'
              : variant === 'gray'
                ? 'bg-muted'
                : variant === 'accent'
                  ? activeStyle.accent
                  : activeStyle.bg,
          )}
          aria-hidden="true"
        />
      )}

      {/* Extension badge — default variant */}
      {label && size !== 'xs' && variant === 'default' && (
        <div
          className={cn(
            'absolute bottom-[15%] start-[2.5%] rounded-[2px] px-[3px] py-[2px] inline-flex items-center',
            activeStyle.bg,
          )}
        >
          <span
            className={cn(
              'font-bold text-white uppercase leading-none',
              badgeFont[size],
            )}
          >
            {label}
          </span>
        </div>
      )}

      {/* Extension label — accent/solid variants */}
      {label && size !== 'xs' && variant !== 'default' && (
        <div
          className={cn(
            'absolute top-[57.5%] bottom-[15%] left-[10%] right-[10%] flex items-center justify-center font-bold uppercase leading-none',
            labelFont[size],
            variant === 'solid'
              ? 'text-white'
              : variant === 'gray'
                ? 'text-secondary-foreground'
                : 'text-accent-foreground',
          )}
        >
          {label}
        </div>
      )}

      {/* Simple file type icon */}
      {Icon && (
        <div
          className={cn(
            'absolute flex items-center justify-center',
            variant === 'default'
              ? 'top-[37.5%] bottom-[17.5%] left-[27.5%] right-[27.5%]'
              : 'top-[40%] bottom-[20%] left-[30%] right-[30%]',
          )}
        >
          <Icon
            className={cn(
              'size-full',
              variant === 'solid'
                ? 'text-white'
                : variant === 'gray'
                  ? 'text-secondary-foreground'
                  : 'text-primary',
            )}
            strokeWidth={2}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}

FileTypeIcon.displayName = 'FileTypeIcon';

export { FileTypeIcon };
